<template>
  <v-card
    v-if="item && Object.keys(item).length > 0"
    flat
    class="printableArea font my-0"
    id="printBill"
    :outlined="outlined"
  >
    <v-card-text class="pa-0">
      <div class="text-center justify-content-center">
        <img
          v-if="Img && show_logo"
          width="70"
          :src="Img"
          alt="logo"
          class="logo"
        />
        <h4
          class="font pt-2 text-capitalize"
          v-html="safeItem?.store_id?.name || $t('store_name')"
        ></h4>
        <div
          v-if="phoneMobileLine"
          class="font default text-center receipt-store-phone"
        >
          {{ phoneMobileLine }}
        </div>
        <div
          v-if="show_store_address && storeAddress"
          class="font default text-center receipt-store-address"
        >
          {{ storeAddress }}
        </div>

        <h3 class="text-center font font-weight-bolder">
          {{ $t("receipt") }}
        </h3>
        <div v-if="queueLabel" class="my-2">
          <div class="font default">{{ $t("queue_number") }}</div>
          <div
            class="font font-weight-black"
            style="font-size: 28px; line-height: 1.1"
          >
            {{ queueLabel }}
          </div>
        </div>
      </div>
      <div class="font default">
        {{ $t("code") }} : <span class="bold">{{ safeItem?.code || "" }}</span>
      </div>
      <div v-if="branch" class="font default">
        {{ $t("branch") }} : {{ branchMsg }}
      </div>
      <v-row class="justify-content-between align-items-end">
        <v-col cols="12" class="font default">
          <div class="font default">
            {{ $t("date") }} : {{ safeItem?.created_at | moment }}
          </div>
          <div class="font default">
            {{ $t("seller") }} :
            {{ safeItem?.user_id?.first_name || $t("unknown") }}
          </div>
          <div class="font default" v-if="safeItem?.table_id">
            {{ $t("table") }} : {{ safeItem?.table_id?.name || $t("unknown") }}
          </div>
          <div class="font default" v-if="hasBuffetPlans">
            {{ $t("buffet_persons") }} : {{ buffetTotalPersons }}
          </div>
          <div class="font default" v-if="show_payment_method">
            {{ $t("payment_method") }} : {{ $t(payment_method_text) }}
          </div>
          <template v-if="item.customer_id">
            <div class="font default">
              {{ $t("customer") }} : {{ customer }}
              <span v-if="customer_phone">
                |
                {{ customerPhone }}
              </span>
              <span v-if="customer_point"> | {{ customerPoint }}</span>
            </div>

            <div v-if="customer_address && address" class="font default">
              {{ $t("address") }} : {{ address }}
            </div>
            <div v-if="customer_logistics" class="font default">
              {{ $t("logistics_partner") }} : {{ customerLogistics }}
            </div>
          </template>
          <div
            v-if="receipt_show_stamp_card && stampCardReceipt"
            class="font default receipt-stamp-card"
          >
            <div>
              {{ $t("receipt_stamp_card") }}:
              <span class="receipt-stamp-dots" aria-hidden="true">{{
                stampProgressDots
              }}</span>
              {{ stampCardReceipt.current }}/{{ stampCardReceipt.required }}
            </div>
          </div>
        </v-col>
      </v-row>

      <hr class="dot" />
      <v-simple-table class="bill-table" v-if="receipt_size === '80mm'">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="font text-left">{{ $t("item") }}</th>
              <th class="font text-center">
                {{ $t("amount") }}
              </th>
              <th class="font text-right">{{ $t("price") }}</th>
              <th class="font text-right">
                {{ $t("total") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(data, index) in safeItem?.history || []" :key="index">
              <td class="font text-left">
                {{ getProductDisplayName(data) }}
                <span
                  v-if="isStampRewardLine(safeItem, data)"
                  class="font stamp-reward-tag"
                >
                  ({{ $t("stamp_paid_by_stamp") }})
                </span>
              </td>
              <td class="font text-center">
                {{ data.qty | ToNumber }}
              </td>
              <td class="font text-right">
                <template v-if="isStampRewardLine(safeItem, data)">
                  {{ $t("free") }}
                </template>
                <template v-else>
                  {{ data.price_sale | NumberFormat }}
                </template>
              </td>
              <td class="font text-right">
                <template v-if="isStampRewardLine(safeItem, data)">
                  {{ $t("free") }}
                </template>
                <template v-else>
                  {{ data.total_price | NumberFormat }}
                </template>
              </td>
            </tr>
            <template v-if="hasBuffetPlans">
              <tr class="receipt-buffet-heading">
                <td colspan="4" class="font text-left font-weight-bold pt-1">
                  {{ $t("buffet_plans") }}
                </td>
              </tr>
              <tr
                v-for="(plan, idx) in receiptBuffetPlans"
                :key="`bp80-${idx}`"
              >
                <td class="font text-left">
                  {{ buffetPlanName(plan) }}
                </td>
                <td class="font text-center">
                  {{ plan.persons | ToNumber }}
                </td>
                <td class="font text-right">
                  {{ buffetPlanUnitPrice(plan) | NumberFormat }}
                </td>
                <td class="font text-right">
                  {{ buffetPlanSubtotal(plan) | NumberFormat }}
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
      <template v-else>
        <template v-if="safeItem?.history && safeItem?.history.length > 0">
          <v-list-item
            class="font default text-left pa-0"
            dense
            v-for="(data, index) in safeItem?.history || []"
            :key="index"
          >
            <v-list-item-content class="py-0 px-0">
              <v-list-item-title class="font default">
                {{ getProductDisplayName(data) }}
                <span
                  v-if="isStampRewardLine(safeItem, data)"
                  class="font stamp-reward-tag"
                >
                  ({{ $t("stamp_paid_by_stamp") }})
                </span>
              </v-list-item-title>
              <v-list-item-subtitle class="font default">
                <template v-if="isStampRewardLine(safeItem, data)">
                  {{ data.qty | ToNumber }} x {{ $t("free") }}
                </template>
                <template v-else>
                  {{ data.qty | ToNumber }} x
                  {{ data.price_sale | NumberFormat }}
                </template>
              </v-list-item-subtitle>
              <v-list-item-subtitle
                class="font default"
                v-if="data.bundle_stock_logs"
              >
                <v-list-item-subtitle
                  class="font default"
                  v-for="(bundle, index) in data.bundle_stock_logs"
                  :key="index"
                >
                  {{ bundle.bundle_item_product.name }} x
                  {{ bundle.bundle_qty }}
                </v-list-item-subtitle>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="font default">
              <template v-if="isStampRewardLine(safeItem, data)">
                {{ $t("free") }}
              </template>
              <template v-else>
                {{ data.total_price | NumberFormat }}
              </template>
            </v-list-item-action>
          </v-list-item>
        </template>
      </template>
      <hr class="dot" />

      <template v-if="hasBuffetPlans && receipt_size !== '80mm'">
        <div class="font default font-weight-bold">
          {{ $t("buffet_plans") }}
        </div>
        <v-list-item
          v-for="(plan, idx) in receiptBuffetPlans"
          :key="`bp-${idx}`"
          class="font default text-left pa-0 receipt-buffet-line"
          dense
        >
          <v-list-item-content class="py-0 px-0">
            <v-list-item-title class="font default">
              {{ buffetPlanName(plan) }}
            </v-list-item-title>
            <v-list-item-subtitle class="font default">
              {{ plan.persons | ToNumber }} x
              {{ buffetPlanUnitPrice(plan) | NumberFormat }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="font default">
            {{ buffetPlanSubtotal(plan) | NumberFormat }}
          </v-list-item-action>
        </v-list-item>
        <hr class="dot" />
      </template>

      <div class="printClass font default" v-if="subtotal">
        <div>{{ $t("subtotal") }}</div>
        <div class="font-weight-bold--text">{{ subtotal }}</div>
      </div>
      <!-- cash back -->
      <div class="printClass font default" v-if="cash_back">
        <div>{{ $t("cash_back") }}</div>
        <div class="font-weight-bold--text">
          {{ cash_back }}
        </div>
      </div>
      <div class="printClass font default" v-if="safeItem?.total_discount">
        <div>{{ $t("discount") }}</div>
        <div class="font-weight-bold--text">{{ total_discount }}</div>
      </div>
      <div class="printClass font default" v-if="tax_include">
        <div>VAT {{ tax }}</div>
        <div class="font-weight-bold--text">
          {{ safeItem?.tax_amount | NumberFormat }}
        </div>
      </div>
      <!-- thb_currency -->
      <div class="printClass font default" v-if="thb_currency">
        <div>THB</div>
        <div class="font-weight-bold--text">
          {{ thRate }}
        </div>
      </div>
      <!-- usd_currency -->
      <div class="printClass font default" v-if="usd_currency">
        <div>USD</div>
        <div class="font-weight-bold--text">
          {{ usRate }}
        </div>
      </div>

      <div class="printClass font default font-weight-bold">
        <div>{{ $t("grand_total") }}</div>
        <div class="font-weight-bold--text">
          {{ grand_total }} {{ currency }}
        </div>
      </div>

      <!-- Show payment currency if different from product currency -->
      <div
        v-if="showPaymentCurrency"
        class="printClass font default font-weight-bold"
      >
        <div>{{ $t("total_paid") }}</div>
        <div class="font-weight-bold--text">
          {{ total_paid_formatted }} {{ paymentCurrency }}
        </div>
      </div>

      <template v-if="item.note">
        <div class="printClass font default text-left font-weight-bold">
          {{ $t("noted") }}
        </div>
        <span class="printClass font default text-left"
          >- {{ safeItem?.note }}</span
        >
      </template>
      <template>
        <barcode
          v-if="barcode"
          class="barcode"
          :displayValue="false"
          :value="safeItem?.code || ''"
          :width="2"
          :height="35"
        />
      </template>

      <div class="font text-center" v-html="footer_text"></div>

      <!-- qr -->
      <div v-if="show_qr" class="text-center" style="margin-top: 10px">
        <img width="100" :src="urlQR" alt="qr" />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import VueBarcode from "vue-barcode";
import { formatBillQueue } from "@/utils/billQueue";

export default {
  mixins: [myMixin],
  components: {
    barcode: VueBarcode,
  },
  props: {
    item: Object,
    show_logo: Boolean,
    branch: Boolean,
    show_phone_number: Boolean,
    show_mobile_number: Boolean,
    show_store_address: Boolean,
    customer_point: Boolean,
    customer_phone: Boolean,
    customer_address: Boolean,
    thb_currency: Boolean,
    usd_currency: Boolean,
    tax_include: Boolean,
    barcode: Boolean,
    show_qr: Boolean,
    footer_text: String,
    receipt_size: String,
    customer_logistics: Boolean,
    show_payment_method: Boolean,
    receipt_show_stamp_card: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    queueLabel() {
      return formatBillQueue(this.item?.queue_no);
    },
    // Ensure item has required structure
    safeItem() {
      if (!this.item) return null;

      // Ensure store_id exists
      if (!this.item.store_id) {
        this.item.store_id = { name: this.$t("store_name") };
      }

      // Ensure user_id exists
      if (!this.item.user_id) {
        this.item.user_id = { first_name: this.$t("unknown") };
      }

      // Ensure history exists and has proper structure
      if (this.item.history) {
        this.item.history = this.item.history.map((historyItem) => {
          if (!historyItem.product_id) {
            historyItem.product_id = { name: this.$t("product_name") };
          }
          return historyItem;
        });
      }

      return this.item;
    },
    receiptBuffetPlans() {
      const r = this.safeItem;
      if (!r) return [];
      if (Array.isArray(r.buffet_plans) && r.buffet_plans.length) {
        return r.buffet_plans;
      }
      if (Array.isArray(r.buffetPlans) && r.buffetPlans.length) {
        return r.buffetPlans;
      }
      return [];
    },
    hasBuffetPlans() {
      return this.receiptBuffetPlans.length > 0;
    },
    buffetTotalPersons() {
      return this.receiptBuffetPlans.reduce(
        (sum, p) => sum + (Number(p.persons) || 0),
        0,
      );
    },
    // Format product name with options
    getProductDisplayName() {
      return (data) => {
        if (!data) {
          return this.$t("product_name");
        }

        // Handle both data.product_id and data.product structures
        // product_id can be either a number or an object
        let product = null;

        if (data.product) {
          product = data.product;
        } else if (data.product_id && typeof data.product_id === "object") {
          product = data.product_id;
        }

        if (!product || !product.name) {
          return this.$t("product_name");
        }

        let productName = product.name;

        // Check if product has variations
        const variant = data.product_variant;
        if (variant) {
          let options = [];

          // Try to get options from optionValues (object format)
          if (
            variant.optionValues &&
            typeof variant.optionValues === "object"
          ) {
            options = Object.values(variant.optionValues);
          }
          // Fallback to options array format
          else if (variant.options && Array.isArray(variant.options)) {
            options = variant.options.map((option) => option.selected_value);
          }

          if (options.length > 0) {
            productName = `${productName} (${options.join(", ")})`;
          }
        }

        return productName;
      };
    },
    urlQR() {
      if (this.user && this.user.stores && this.user.stores.qr) {
        // check if contains 'com'
        if (this.user.stores.qr.includes("com")) {
          return this.user.stores.qr;
        }
        const baseURL = process.env.VUE_APP_BASE_URL;
        return `${baseURL}/uploads/${this.user.stores.qr}`;
      } else {
        return null;
      }
    },
    Img() {
      if (this.user && this.user.stores && this.user.stores.logo) {
        // check logo is contain default string or not
        if (!this.user.stores.logo.includes("default")) {
          return Help.img(this.user.stores.logo);
        }
      }
    },

    branchMsg() {
      if (this.safeItem?.branch_id && this.safeItem.branch_id.name) {
        return `${this.safeItem.branch_id.name}`;
      }
      return "";
    },

    customer() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.first_name) {
        return `${this.safeItem.customer_id.first_name}`;
      }
      return "";
    },
    customerPoint() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.point) {
        return `${this.$options.filters.NumberFormat(
          this.safeItem.customer_id.point,
        )}`;
      }
      return "";
    },

    customerPhone() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.phone_no) {
        return this.safeItem.customer_id.phone_no;
      }
      return "";
    },

    address() {
      if (
        this.safeItem?.customer_id?.village?.vill_name &&
        this.safeItem?.customer_id?.village?.district?.dr_name &&
        this.safeItem?.customer_id?.village?.district?.province?.pr_name
      ) {
        const village = this.safeItem.customer_id.village;
        const district = village.district;
        const province = district.province;
        return `${village.vill_name}, ${district.dr_name}, ${province.pr_name}`;
      }
      return "";
    },

    customerLogistics() {
      let message = "";
      if (this.safeItem?.customer_id?.logistics?.name) {
        message += this.safeItem.customer_id.logistics.name;
      }
      if (this.safeItem?.customer_id?.address) {
        message += " - " + this.safeItem.customer_id.address;
      }
      return message;
    },

    stampCardReceipt() {
      return this.safeItem?.stamp_card_receipt || null;
    },

    stampProgressDots() {
      const r = this.stampCardReceipt;
      if (!r) {
        return "";
      }
      const req = Math.max(1, parseInt(r.required, 10) || 10);
      const cur = parseInt(r.current, 10) || 0;
      const filled = Math.min(cur, req);
      const empty = Math.max(0, req - filled);
      return "\u25CF".repeat(filled) + "\u25CB".repeat(empty);
    },

    thRate() {
      if (
        this.safeItem &&
        this.safeItem.store_id &&
        this.safeItem.store_id.thb
      ) {
        const thb = this.safeItem.store_id.thb;
        const total = this.safeItem.total_price;
        return this.$options.filters.NumberFormat(total / thb);
      }
      return 0;
    },
    usRate() {
      if (
        this.safeItem &&
        this.safeItem.store_id &&
        this.safeItem.store_id.usd
      ) {
        const usd = this.safeItem.store_id.usd;
        const total = this.safeItem.total_price;
        return this.$options.filters.NumberFormat(total / usd);
      }
      return 0;
    },
    cash_back() {
      // Cash back = Amount paid by customer - Final amount owed
      // total_price already includes discount applied (total_price = total_price_sale - total_discount)
      if (
        this.safeItem &&
        this.safeItem.total_price &&
        this.safeItem.total_paid
      ) {
        let amountOwedInPaymentCurrency = this.safeItem.total_price;
        const amountPaid = this.safeItem.total_paid;

        // Check if product currency differs from payment currency
        const productCurrency = this.currency; // Product currency (THB, LAK, USD)
        const paymentCurrency = this.paymentCurrency; // Currency customer paid in

        if (productCurrency !== paymentCurrency) {
          // Cross-currency transaction - need to convert product price to payment currency
          if (productCurrency === "THB" && paymentCurrency === "LAK") {
            // THB to LAK: multiply by exchange rate
            const thbRate = this.safeItem.store_id?.thb || 690;
            amountOwedInPaymentCurrency = this.safeItem.total_price * thbRate;
          } else if (productCurrency === "USD" && paymentCurrency === "LAK") {
            // USD to LAK: multiply by exchange rate
            const usdRate = this.safeItem.store_id?.usd || 21000;
            amountOwedInPaymentCurrency = this.safeItem.total_price * usdRate;
          } else if (productCurrency === "LAK" && paymentCurrency === "THB") {
            // LAK to THB: divide by exchange rate
            const thbRate = this.safeItem.store_id?.thb || 690;
            amountOwedInPaymentCurrency = this.safeItem.total_price / thbRate;
          } else if (productCurrency === "LAK" && paymentCurrency === "USD") {
            // LAK to USD: divide by exchange rate
            const usdRate = this.safeItem.store_id?.usd || 21000;
            amountOwedInPaymentCurrency = this.safeItem.total_price / usdRate;
          } else if (productCurrency === "THB" && paymentCurrency === "USD") {
            // THB to USD: THB -> LAK -> USD
            const thbRate = this.safeItem.store_id?.thb || 690;
            const usdRate = this.safeItem.store_id?.usd || 21000;
            const lakAmount = this.safeItem.total_price * thbRate;
            amountOwedInPaymentCurrency = lakAmount / usdRate;
          } else if (productCurrency === "USD" && paymentCurrency === "THB") {
            // USD to THB: USD -> LAK -> THB
            const thbRate = this.safeItem.store_id?.thb || 690;
            const usdRate = this.safeItem.store_id?.usd || 21000;
            const lakAmount = this.safeItem.total_price * usdRate;
            amountOwedInPaymentCurrency = lakAmount / thbRate;
          }
        }

        // Calculate cash back
        const cashBack = amountPaid - amountOwedInPaymentCurrency;

        // Only show cash back if customer paid more than owed
        // Use small tolerance for floating point comparison
        const tolerance = 1; // Use 1 for LAK (since it's a large denomination)
        if (cashBack > tolerance) {
          return this.$options.filters.NumberFormat(cashBack);
        }
      }
      return 0;
    },
    subtotal() {
      if (this.safeItem && this.safeItem.total_price_sale) {
        // Show the full price before discount
        return this.$options.filters.NumberFormat(
          this.safeItem.total_price_sale,
        );
      }
      return 0;
    },
    total_discount() {
      if (this.safeItem && this.safeItem.total_discount) {
        return `- ${this.$options.filters.NumberFormat(
          this.safeItem.total_discount,
        )}`;
      }
      return 0;
    },
    total_paid() {
      if (
        this.safeItem &&
        this.safeItem.total_paid &&
        this.safeItem.total_price
      ) {
        if (this.safeItem.total_paid != this.safeItem.total_price) {
          // Since backend now stores original prices, no conversion needed
          return this.$options.filters.NumberFormat(this.safeItem.total_paid);
        }
        return 0;
      }
      return 0;
    },
    grand_total() {
      if (this.safeItem && this.safeItem.total_price) {
        return this.$options.filters.NumberFormat(this.safeItem.total_price);
      }
      return 0;
    },
    currency() {
      // Get product currency from history if available
      if (
        this.safeItem &&
        this.safeItem.history &&
        this.safeItem.history.length > 0 &&
        this.safeItem.history[0].product &&
        this.safeItem.history[0].product.currency
      ) {
        return this.safeItem.history[0].product.currency.code;
      }
      // Fallback to ticket currency
      if (
        this.safeItem &&
        this.safeItem.currency_id &&
        this.safeItem.currency_id.code
      ) {
        return this.safeItem.currency_id.code;
      }
      return "LAK"; // Default to LAK
    },
    // Payment currency - shows the currency the customer actually paid in
    paymentCurrency() {
      if (
        this.safeItem &&
        this.safeItem.total_paid &&
        this.safeItem.total_price
      ) {
        const ratio = this.safeItem.total_paid / this.safeItem.total_price;

        // If ratio is very small (< 0.1), customer likely paid in higher value currency
        if (ratio < 0.1) {
          // Check if it matches THB conversion (around 0.0016 for 630 rate)
          if (ratio > 0.001 && ratio < 0.01) {
            return "THB";
          }
          // Check if it matches USD conversion (around 0.00004 for 23300 rate)
          else if (ratio < 0.0001) {
            return "USD";
          }
        }
        // If ratio is close to 1, customer paid in same currency
        else if (ratio > 0.9 && ratio < 1.1) {
          return this.currency;
        }
        // If ratio is very large, customer likely paid in lower value currency
        else if (ratio > 10) {
          return "LAK";
        }
      }
      // Default to product currency
      return this.currency;
    },

    // Check if we should show payment currency (when different from product currency)
    showPaymentCurrency() {
      return (
        this.paymentCurrency !== this.currency && this.safeItem?.total_paid
      );
    },

    // Format the total paid amount
    total_paid_formatted() {
      if (this.safeItem && this.safeItem.total_paid) {
        return this.$options.filters.NumberFormat(this.safeItem.total_paid);
      }
      return "0";
    },

    payment_method_text() {
      if (this.safeItem && this.safeItem.type) {
        return this.safeItem.type;
      }
      return "";
    },

    storePhone() {
      if (this.safeItem?.store_id?.phone_no) {
        return this.safeItem.store_id.phone_no;
      }
      return "";
    },

    storeMobile() {
      if (this.safeItem?.store_id?.mobile_no) {
        return this.safeItem.store_id.mobile_no;
      }
      return "";
    },

    storeAddress() {
      if (this.safeItem?.store_id?.address) {
        return this.safeItem.store_id.address;
      }
      return "";
    },

    phoneMobileLine() {
      const parts = [];
      if (this.show_phone_number && this.storePhone) {
        parts.push(this.storePhone);
      }
      if (this.show_mobile_number && this.storeMobile) {
        parts.push(this.storeMobile);
      }
      return parts.join(" / ");
    },
  },

  methods: {
    buffetPlanName(plan) {
      if (!plan) {
        return this.$t("product_name");
      }
      return (
        plan.plan_name ||
        plan.buffet_plan_name ||
        plan.name ||
        this.$t("product_name")
      );
    },
    buffetPlanUnitPrice(plan) {
      if (!plan) {
        return 0;
      }
      const v = plan.unit_price ?? plan.unitPrice ?? plan.buffet_plan_price;
      return Number(v) || 0;
    },
    buffetPlanSubtotal(plan) {
      if (!plan) {
        return 0;
      }
      if (plan.subtotal != null && plan.subtotal !== "") {
        return Number(plan.subtotal) || 0;
      }
      const persons = Number(plan.persons) || 0;
      return persons * this.buffetPlanUnitPrice(plan);
    },
  },
};
</script>

<style scoped>
.stamp-reward-tag {
  font-weight: bold;
  margin-left: 4px;
}
</style>
